(ns ui.view.transaction-process.model
  (:require
   [common.schema.entities.transaction-process :as tp-schema]
   [common.util :as cu]
   [common.transaction-process.resolver :as resolver]))

;; Root path is dynamic: [:transaction-process <process-id>]
;; All state for a given TP instance lives under this path.

(defn root-path
  [process-id]
  [:transaction-process process-id])

(def initialized-path [:initialized?])

(defn initialized-full-path [state-path]
  (into state-path initialized-path))

(def process-id-path [:process-id])

(defn process-id-full-path [state-path]
  (into state-path process-id-path))

(def server-state-path [:server-state])

(defn server-state-full-path [state-path]
  (into state-path server-state-path))

(def not-found-path [:not-found?])

(defn not-found-full-path [state-path]
  (into state-path not-found-path))

(def draft-path [:draft])

(defn draft-full-path [state-path]
  (into state-path draft-path))

(def saving-path [:saving?])

(defn saving-full-path [state-path]
  (into state-path saving-path))

;; Tracks an in-flight save so we can clear draft entries once the server
;; reflects them. Reconciliation is per-field: each entry in :fields is
;; retired once server-state holds an equivalent value. Multi-field saves
;; commit as separate txs whose pushes arrive one at a time, so we can't
;; clear every saved field on the first push.
;;
;; Shape:
;;   nil — no save in flight
;;   {:fields {attr sent-value}
;;    :ok?    <all field updates acked by backend>}
(def in-flight-path [:in-flight])

(defn in-flight-full-path [state-path]
  (into state-path in-flight-path))

;; UI state

(def ui-path [:ui])

(defn ui-full-path [state-path]
  (into state-path ui-path))

(def sidebar-collapsed-path [:ui :sidebar-collapsed?])

(defn sidebar-collapsed-full-path [state-path]
  (into state-path sidebar-collapsed-path))

(def show-permissions-path [:ui :show-permissions?])

(defn show-permissions-full-path [state-path]
  (into state-path show-permissions-path))

(def show-details-path [:ui :show-details?])

(defn show-details-full-path [state-path]
  (into state-path show-details-path))

(def detail-panel-path [:ui :detail-panel])

(defn detail-panel-full-path [state-path]
  (into state-path detail-panel-path))

(def modal-path [:ui :modal])

(defn modal-full-path [state-path]
  (into state-path modal-path))

;; Dev toggle path (global, not per-process)
(def skip-mandatory-validation-path [:dev :skip-mandatory-validation?])

;; Dev impersonation (global, not per-process). Frontend-only: overrides the
;; permission-resolver inputs so a dev can see the form as another role without
;; logging in/out. Shape: {:active? bool :global-role :admin|:user|:read-only
;; :team-roles #{:investment-team :platform-team :portfolio-manager}}.
(def impersonation-path [:dev :impersonation])

(def impersonatable-team-roles
  "The CapMan team-roles a dev can impersonate. Single source for the dev panel
   and the perm-input synthesizer below."
  [:investment-team :platform-team :portfolio-manager])

(defn impersonation-perm-inputs
  "Map the active dev impersonation (or nil) onto the permission-resolver inputs.
   When inactive, returns the real values unchanged. When active, :global-role
   drives admin?/read-only?, and :team-roles becomes a synthetic team-assignments
   map so `resolve-account-field-permission` resolves the account to exactly those
   roles. A placeholder member in the non-selected roles keeps the map non-empty,
   so the 'no team assigned yet ⇒ :w' fallback never fires while impersonating."
  [{:keys [impersonation account-id team-assignments read-only-user? admin?]}]
  (if-not (:active? impersonation)
    {:admin?           admin?
     :read-only-user?  read-only-user?
     :account-id       account-id
     :team-assignments team-assignments}
    (let [roles (set (:team-roles impersonation))]
      {:admin?           (= :admin (:global-role impersonation))
       :read-only-user?  (= :read-only (:global-role impersonation))
       :account-id       account-id
       :team-assignments (into {}
                               (map (fn [role]
                                      [role (if (contains? roles role)
                                              [{:value account-id}]
                                              [{:value ::dev-placeholder}])]))
                               impersonatable-team-roles)})))

;; Pure state helpers

(defn- field-hidden?
  [field-config value-lookup]
  (not (resolver/field-visible? field-config value-lookup)))

(defn hidden-field-clears
  "Draft updates ({field-kw nil}) that clear every template field which is now
   hidden under `entity-data` but still holds a (non-blank) value. Run after each
   draft edit so a conditionally-visible field's value doesn't linger — and get
   saved — once its controlling field is toggled off. CapMan 2026-06: switching
   Equity participation True→False left the participation type/notes populated;
   this clears them (and any analogous hidden dependent, e.g. debt sub-fields when
   debt financing is set to No). Purely data-driven off the template's
   visibility rules — no per-field hardcoding."
  [{:keys [template entity-data]}]
  (->> (:template/fields template)
       (keep (fn [[fkw cfg]]
               (let [v (get entity-data fkw)]
                 (when (and (field-hidden? cfg entity-data)
                            (some? v)
                            (not (and (string? v) (cu/blank-value? v))))
                   [fkw nil]))))
       (into {})))

(defn terminal?
  "True when the TP is completed or rejected. Terminal processes stay locked
   until explicitly reopened."
  [process]
  (tp-schema/terminal-status? (:transaction-process/status process)))

(defn create-initial-state
  [process-id]
  {:initialized? true
   :process-id   process-id
   :server-state nil
   :not-found?   false
   :draft        {}
   :saving?      false
   :in-flight    nil
   :ui           {:sidebar-collapsed? false
                  :show-permissions?  false
                  :show-details?      false
                  :detail-panel       nil
                  :modal              nil}})

(defn- values-equivalent?
  "Coercion-tolerant equality for comparing a value we sent against the value
   the backend serialized back. The only round-trip mismatch we observe in
   practice is multi-cardinality: a vector/list we sent comes back as a set
   (datomic cardinality-many + backend/db/utils/serialize-data). Everything
   else — scalars, keywords, refs (as db-ids), edn-decoded maps — round-trips
   with normal ="
  [sent server]
  (or (= sent server)
      (and (string? sent) (cu/blank-value? sent) (nil? server))
      (and (sequential? sent) (set? server) (= (set sent) server))
      (and (set? sent) (sequential? server) (= sent (set server)))))

(defn reconcile-after-save
  "Pure state transformer. For every saved field the server now reflects,
   drop the entry from :in-flight; if the user has not re-edited since the
   save, also drop the matching draft entry.

   Per-field reconciliation handles three realities:
   - multi-field saves commit as N txs that push incrementally; clearing a
     field too early would flash stale server values for fields not yet pushed
   - backend coercion can reshape sent values (vector → set); values-equivalent?
     accepts those round-trips
   - backend no-ops identical saves without bumping :common/updated-on, so we
     cannot rely on timestamp advance — per-field equivalence catches this too"
  [state]
  (let [{:keys [in-flight server-state draft]} state
        {:keys [fields ok?]} in-flight]
    (if (and in-flight ok? (seq fields))
      (let [confirmed (->> fields
                           (filter (fn [[k sent-v]]
                                     (values-equivalent? sent-v (get server-state k))))
                           (map key)
                           set)
            new-draft (reduce-kv
                       (fn [acc k v]
                         (if (and (contains? confirmed k) (= v (get fields k)))
                           acc
                           (assoc acc k v)))
                       {}
                       draft)
            remaining (apply dissoc fields confirmed)]
        (-> state
            (assoc :draft new-draft)
            (assoc :in-flight (when (seq remaining)
                                (assoc in-flight :fields remaining)))))
      state)))

(defn start-in-flight
  "Snapshot the current save: capture what we're sending, flag saving?. Draft
   is left untouched so the view keeps rendering user values until the server
   confirms them field-by-field."
  [state fields]
  (-> state
      (assoc :saving? true)
      (assoc :in-flight {:fields fields
                         :ok?    false})))
